import React from 'react'
import styles from './Footer.module.css'
import { APP_CONFIG, FOOTER_LINKS, SOCIAL_LINKS } from '@/constants'

/* ────────────────────────── Inline SVG Icons ────────────────────────── */
const PhoneIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.6 3.42 2 2 0 0 1 3.57 1h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 8.2a16 16 0 0 0 5.89 5.89l.72-.72a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 21 16z"/>
  </svg>
)
const MailIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
)
const MapPinIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
)

/** Compact social icons (font-glyph-level simplicity) */
const SOCIAL_ICONS: Record<string, React.ReactNode> = {
  facebook:  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>,
  twitter:   <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/></svg>,
  instagram: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>,
  youtube:   <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58 2.78 2.78 0 0 0 1.95 1.96C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z"/><polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="white"/></svg>,
}

const Footer: React.FC = () => {
  const year = APP_CONFIG.copyrightYear

  return (
    <footer className={styles.footer} aria-label="Site footer">
      {/* ── Main body ── */}
      <div className={styles.footer__body}>
        <div className={styles.footer__grid}>

          {/* Column 1 — Brand & Contact */}
          <div className={styles.footer__brand}>
            <div className={styles.footer__logo}>
              <div className={styles.footer__logoCard}>
                <img
                  src="/assets/lalpathlabs-logo-white.png"
                  alt="Dr Lal PathLabs"
                  className={styles.footer__logoImg}
                />
              </div>
              <div className={styles.footer__partnerInfo}>
                <span className={styles.footer__partnerTag}>Authorized Center</span>
                <span className={styles.footer__partnerName}>Nextgen Health Care</span>
              </div>
            </div>

            <p className={styles.footer__tagline}>
              Your trusted authorized collection center for Dr Lal PathLabs.
              Delivering accurate diagnostic results with care and precision.
            </p>

            <div className={styles.footer__contact}>
              <a href={`tel:${APP_CONFIG.phone}`} className={styles.footer__contactItem}>
                <PhoneIcon /> {APP_CONFIG.phone}
              </a>
              <a href={`mailto:${APP_CONFIG.email}`} className={styles.footer__contactItem}>
                <MailIcon /> {APP_CONFIG.email}
              </a>
              <span className={styles.footer__contactItem}>
                <MapPinIcon /> {APP_CONFIG.address}
              </span>
            </div>

            <div className={styles.footer__accred}>
              <p className={styles.footer__accredText}>
                <strong>Authorized Collection Center</strong><br />
                Accredited partner of Dr Lal PathLabs Ltd.<br />
                NABL accredited · ISO 9001:2015 certified
              </p>
            </div>
          </div>

          {/* Column 2 — Quick Links */}
          <div className={styles.footer__linkGroup}>
            <span className={styles.footer__linkTitle}>Quick Links</span>
            <nav className={styles.footer__links} aria-label="Quick links">
              {FOOTER_LINKS.quickLinks.map((link) => (
                <a key={link.href} href={link.href} className={styles.footer__link}>
                  {link.label}
                </a>
              ))}
            </nav>
          </div>

          {/* Column 3 — Support */}
          <div className={styles.footer__linkGroup}>
            <span className={styles.footer__linkTitle}>Support</span>
            <nav className={styles.footer__links} aria-label="Support links">
              {FOOTER_LINKS.support.map((link) => (
                <a key={link.href} href={link.href} className={styles.footer__link}>
                  {link.label}
                </a>
              ))}
            </nav>
          </div>

          {/* Column 4 — Social */}
          <div className={styles.footer__linkGroup}>
            <span className={styles.footer__socialTitle}>Follow Us</span>
            <div className={styles.footer__socialLinks}>
              {SOCIAL_LINKS.map((social) => (
                <a
                  key={social.icon}
                  href={social.href}
                  className={styles.footer__socialLink}
                  aria-label={`Follow us on ${social.label}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {SOCIAL_ICONS[social.icon]}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      <hr className={styles.footer__hr} />

      {/* ── Copyright bar ── */}
      <div className={styles.footer__copyright}>
        <div className={styles.footer__copyrightInner}>
          <p className={styles.footer__copyrightText}>
            &copy; {year} <strong>Nextgen Health Care Services</strong>. All rights reserved.
            Authorized Collection Center of Dr Lal PathLabs Ltd.
          </p>
          <nav className={styles.footer__legalLinks} aria-label="Legal links">
            <a href="#privacy" className={styles.footer__legalLink}>Privacy Policy</a>
            <a href="#terms"   className={styles.footer__legalLink}>Terms of Use</a>
            <a href="#sitemap" className={styles.footer__legalLink}>Sitemap</a>
          </nav>
        </div>
      </div>
    </footer>
  )
}

export default Footer

